import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useFormContext } from 'react-hook-form'
import { Box, Grid, useTheme } from '@mui/material'
import { HHFormAutocompleteField, HHFormSelectField } from 'components/form-fields/v5'
import { RECURRENCE_INTERVALS } from 'settings/constants/billing'
import T from 'T'
import { getRecurrenceText, isDailyRecurrence } from 'utils/service'
import { Z_INDEX } from 'theme/zIndex'
import { getUniqueRecurrenceOptions } from '../utils/recurrenceOptionsHelper'

const EditServiceRecurrenceFrequencySelector = ({ metaFiltersData = {}, showYearly = false }) => {
  const { control, watch, setValue } = useFormContext()
  const theme = useTheme()

  const watchedServiceIntervalKey = watch('serviceInterval')
  const allRecurrenceOptions = getUniqueRecurrenceOptions(metaFiltersData)
  const recurrenceList = showYearly
    ? allRecurrenceOptions
    : allRecurrenceOptions.filter(option => option.recurrenceInterval?.toLowerCase() !== 'year')

  const selectedServiceInterval = recurrenceList.find(option => {
    const optionKey = `${option.recurrenceInterval}-${option.recurrencePer}`
    return optionKey === watchedServiceIntervalKey
  })

  const isDailyInterval = selectedServiceInterval && isDailyRecurrence(selectedServiceInterval.recurrenceInterval)

  const getSimpleOptionLabel = o => `${o}x`
  const getSimpleOptionValue = v => v

  const getServiceIntervalOptionLabel = option => {
    if (typeof option === 'string') {
      const match = recurrenceList.find(r => `${r.recurrenceInterval}-${r.recurrencePer}` === option)
      if (match) return getRecurrenceText(match.recurrencePer, match.recurrenceInterval)
      return option
    }
    return getRecurrenceText(option.recurrencePer, option.recurrenceInterval)
  }

  useEffect(() => {
    if (isDailyInterval) {
      setValue('recurrenceFrequency', '1')
    }
  }, [isDailyInterval, setValue])

  return (
    <Box mb={3}>
      <Grid container spacing={2} alignItems="center">
        <Grid size={6}>
          <HHFormSelectField
            name="recurrenceFrequency"
            label={T.FREQUENCY}
            control={control}
            options={RECURRENCE_INTERVALS}
            size="small"
            fullWidth
            deprecatedLabel={false}
            getOptionLabel={getSimpleOptionLabel}
            getOptionValue={getSimpleOptionValue}
            disabled={isDailyInterval}
            rules={{
              required: {
                value: true,
                message: T.FREQUENCY_REQUIRED,
              },
            }}
            SelectProps={{
              MenuProps: {
                sx: { zIndex: Z_INDEX.MENU(theme) },
              },
            }}
          />
        </Grid>
        <Grid size={6}>
          <HHFormAutocompleteField
            name="serviceInterval"
            control={control}
            label={T.SERVICE_INTERVAL}
            options={recurrenceList}
            size="small"
            getOptionLabel={getServiceIntervalOptionLabel}
            isOptionEqualToValue={(option, value) => {
              if (!value) return false
              const optionKey = `${option.recurrenceInterval}-${option.recurrencePer}`
              return optionKey === value
            }}
            onChangeMiddleware={(onChange, newValue) => {
              const newKey = newValue ? `${newValue.recurrenceInterval}-${newValue.recurrencePer}` : ''
              onChange(newKey)
            }}
            disabled={recurrenceList.length === 0}
            deprecatedLabel={false}
            fullWidth
            rules={{
              required: {
                value: true,
                message: T.SERVICE_INTERVAL_REQUIRED,
              },
            }}
            componentsProps={{
              popper: {
                sx: { zIndex: Z_INDEX.SUBMENU(theme) },
              },
            }}
          />
        </Grid>
      </Grid>
    </Box>
  )
}

EditServiceRecurrenceFrequencySelector.propTypes = {
  metaFiltersData: PropTypes.object,
  showYearly: PropTypes.bool,
}

export default EditServiceRecurrenceFrequencySelector
